// src/vdr/basic.ts
import * as XLSX from "xlsx";
import type { IndexRow } from "./providers"; // from Step 1 (VDR types)

// Accepts codes like "1", "1.1", "1.1.2" (pure digits + dots)
const CODE_RE = /^\d+(?:\.\d+)*$/;

// --- CSV parsing (lenient, same as your current logic) ---
function parseCsv(text: string): IndexRow[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  if (!lines.length) return [];

  // assume first row has headers; be forgiving with header names
  const header = lines[0].split(",").map(h => h.trim().toLowerCase());
  const iCode = header.findIndex(h => ["code","number","index","no"].includes(h));
  const iTitle = header.findIndex(h => ["title","name","folder name"].includes(h));

  const rows: IndexRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",").map(c => c.trim());
    const code = (iCode >= 0 ? cols[iCode] : cols[0])?.trim();
    const title = (iTitle >= 0 ? cols[iTitle] : cols[1])?.trim();
    if (code && CODE_RE.test(code) && title !== undefined) {
      rows.push({ code, title: title ?? "" });
    }
  }
  return rows;
}

// --- XLSX parsing (same as current behavior) ---
async function parseXlsx(file: File): Promise<IndexRow[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const json: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });

  const rows: IndexRow[] = json.map((r) => {
    const lower: Record<string, any> = {};
    Object.keys(r).forEach(k => (lower[k.toLowerCase()] = r[k]));
    const code = String(
      lower.code ?? lower.number ?? lower.index ?? r["Code"] ?? r["Number"] ?? r["Index"] ?? ""
    ).trim();
    const title = String(
      lower.title ?? lower.name ?? lower["folder name"] ?? r["Title"] ?? r["Name"] ?? ""
    ).trim();
    return { code, title };
  }).filter(r => CODE_RE.test(r.code));

  return rows;
}

/**
 * Parse an index file (CSV/XLS/XLSX) into normalized rows.
 * Behavior matches your previous parseIndexFile.
 */
export async function parseIndexFile(file: File): Promise<IndexRow[]> {
  const ext = file.name.toLowerCase().split(".").pop();
  console.log(`🧾 [basic] parseIndexFile: name="${file.name}" ext="${ext}" size=${(file as any)?.size ?? "?"}`);

  let rows: IndexRow[] = [];
  if (ext === "csv") {
    const text = await file.text();
    rows = parseCsv(text);
  } else if (ext === "xlsx" || ext === "xls") {
    rows = await parseXlsx(file);
  } else {
    const text = await file.text();
    rows = parseCsv(text); // fallback: treat as CSV
  }

  console.log(`   ↳ parsed rows: ${rows.length}`);
  if (rows.length > 0) {
    const sample = rows.slice(0, Math.min(3, rows.length));
    console.log("   ↳ sample:", sample);
  } else {
    console.warn("   ⚠️ No valid rows parsed. Expect headers like Code/Number/Index and Title/Name.");
  }
  return rows;
}
